import { Contact } from '../../providers/api/dtos/contact';
import { hasSamePhoneNumber } from './hasSamePhoneNumber';

/**
 * Finds a duplicate contact in a list of potential duplicates
 *
 * A contact is considered a duplicate if it has the same email AND phone number.
 * Phone numbers are compared using normalized comparison (see hasSamePhoneNumber).
 *
 * @param contactValues - The contact values to check for duplicates
 * @param potentialDuplicates - List of potential duplicate contacts
 * @returns The duplicate contact if found, otherwise undefined
 *
 * @example
 * ```typescript
 * const newContact = { email: 'test@example.com', phoneNumber: '333 1234567' };
 * const existingContacts = [
 *   { id: '1', email: 'test@example.com', phoneNumber: '+39 333 1234567' },
 *   { id: '2', email: 'other@example.com', phoneNumber: '333 7654321' }
 * ];
 * const duplicate = findDuplicateContact(newContact, existingContacts);
 * // Returns the contact with id '1' (same email and phone)
 * ```
 */
export const findDuplicateContact = (
  contactValues: Partial<Contact> | undefined,
  potentialDuplicates: Contact[] | undefined,
): Contact | undefined => {
  if (!contactValues || !potentialDuplicates || potentialDuplicates.length === 0) {
    return undefined;
  }

  return potentialDuplicates.find(
    (duplicate) => duplicate.email === contactValues.email && hasSamePhoneNumber(contactValues, duplicate),
  );
};
